/*
 * As we don't comply with the official URL API, we needed to implement our own
 * function to remove parameters from the search.
 * Having said that, we are using the URLSearchParams API to do so.
 */
import { objectEntries, isItEmpty, convertStringToObjectCollection } from '~/common'

export const stringifyParameters = <P extends object = object>(
  parameters: P,
  prefix = '?',
  encode = false // do NOT default to true until all APIs (endpoints) are able to deal with this!
): string => {
  const parameterString = objectEntries<P>(parameters || {})
    .filter(([, value]) => !isItEmpty(value))
    .map(([key, value]) =>
      Array.isArray(value)
        ? value
            .map((entry) =>
              stringifyParameters(
                {
                  [key]: entry,
                },
                ''
              )
            )
            .join('&')
        : `${String(key)}=${encode ? encodeURIComponent(String(value)) : String(value)}`
    )
    .join('&')
  return parameterString ? `${prefix}${parameterString}` : ''
}

export const buildUrlWithParameters = <P extends object = object>(url: string, parameters: P) =>
  `${url}${stringifyParameters(parameters, url.includes('?') ? '&' : '?')}`

export const urlParametersToObjectCollection = (url: string): Record<string, string> =>
  convertStringToObjectCollection(url.substring(url.indexOf('?') + 1), '&', '=', decodeURIComponent)
